// App.js
import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Image,
  Form,
  Navbar,
  Button,
} from 'react-bootstrap';
import fetchPrediction from './APIUtils/imageApi';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css'; // Import the CSS file

function App() {
  // State variables
  const [chosenPicture, setChosenPicture] = useState(null);
  const [containerCode, setContainerCode] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [searching, setSearching] = useState(false);
  const [croppedImages, setCroppedImages] = useState([]);

  // Event Handlers
  const handlePicture = async (event) => {
    try {
      const file = event.target.files[0];
      await handleFile(file);
    } catch (error) {
      setContainerCode('Error handling picture');
      console.error('Error handling picture:', error);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    await handleFile(file);
    setDragOver(false);
  };

  // File Handling
  const handleFile = async (file) => {
    setCroppedImages([]);
    setContainerCode(null);
    setSearching(true);
    try {
      const url = await readFileAsDataURL(file);
      setImgUrl(url);
      setChosenPicture(file);

      const formData = new FormData();
      formData.append('image', file);
      const data = await fetchPrediction(formData);

      if (data.images) {
        setCroppedImages(data.images);
      }

      if (data.code === 400) {
        setContainerCode(data.message);
      } else {
        setContainerCode(data.serial_number);
      }
    } catch (error) {
      setContainerCode('Error handling file');
      console.error('Error handling file:', error);
    } finally {
      setSearching(false);
    }
  };

  const readFileAsDataURL = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  // TestOption Component
  const TestOption = () => {
    const testImages = [
      { name: 'blue-container.jpg', src: '/test-images/blue-container.jpg' },
      { name: 'driveway-snap.jpeg', src: '/test-images/driveway-snap.jpeg' },
      { name: 'green-container.webp', src: '/test-images/green-container.webp' },
    ];

    const handleTestImageClick = async (src) => {
      setCroppedImages([]);
      setContainerCode(null);
      setSearching(true);
      try {
        const response = await fetch(src);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const blob = await response.blob();
        const filename = src.substring(src.lastIndexOf('/') + 1); // Extract 'test1.jpg' from '/test-images/test1.jpg'
        const file = new File([blob], filename, { type: blob.type });
        await handleFile(file);
      } catch (error) {
        setContainerCode('Error loading test image');
        console.error('Error loading test image:', error);
      } finally {
        setSearching(false);
      }
    };

    return (
      <Col xs={12} className="mb-4">
        <div className="test-option-section p-3 border rounded shadow-sm bg-light">
          <h4 className="mb-3 text-center">Test with Sample Images</h4>
          <Row>
            {testImages.map((image, index) => (
              <Col
                key={index}
                xs={4}
                sm={4}
                md={4}
                className="d-flex justify-content-center mb-3"
              >
                <Button
                  variant="outline-primary"
                  className="p-0 border-0"
                  onClick={() => handleTestImageClick(image.src)}
                >
                  <Image
                    src={image.src}
                    alt={`Test Image ${index + 1}`}
                    thumbnail
                    className="test-image"
                  />
                </Button>
              </Col>
            ))}
          </Row>
        </div>
      </Col>
    );
  };

  // Components
  const FormComponent = ({ imgUrl, handlePicture }) => (
    <Col xs={12} sm={10} md={12} className="mb-4">
      <div
        className={`upload-area p-4 text-center border rounded ${
          dragOver ? 'border-primary' : 'border-secondary'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Form.Label htmlFor="file-input" className="w-50 h-50 m-0">
          {imgUrl ? (
            <Image
              src={imgUrl}
              alt="Chosen"
              fluid
              className="chosen-image"
            />
          ) : (
            <div className="d-flex flex-column justify-content-center align-items-center h-100">
              <i
                className="bi bi-cloud-arrow-up-fill"
                style={{ fontSize: '3rem', color: '#0d6efd' }}
              ></i>
              <p className="mt-3 text-muted">
                Drag & Drop an image here or click to select a file
              </p>
            </div>
          )}
        </Form.Label>
        <Form.Control
          id="file-input"
          type="file"
          accept="image/*"
          onChange={handlePicture}
          className="d-none"
        />
      </div>
    </Col>
  );

  const CroppedImagesComponent = ({ containerCode, croppedImages }) => (
    <>
      {containerCode && croppedImages.length > 0 && (
        <Col xs={12} className="mb-4">
          <Row className="justify-content-center">
            {croppedImages.map((img, index) => (
              <Col
                key={index}
                xs={6}
                sm={6}
                md={4}
                lg={3}
                className="mb-3 d-flex justify-content-center"
              >
                <div className="image-container">
                  <Image
                    src={img}
                    alt={`Cropped image ${index + 1}`}
                    thumbnail
                    className={index === 0 ? 'border border-warning' : ''}
                    fluid
                  />
                </div>
              </Col>
            ))}
          </Row>
        </Col>
      )}
    </>
  );

  const ResultComponent = ({ containerCode, searching }) => (
    <Col xs={12} className="d-flex justify-content-center mt-4">
      {searching && (
        <Spinner animation="border" variant="light" role="status">
          <span className="visually-hidden">Processing...</span>
        </Spinner>
      )}

      {containerCode && !searching && (
        <Alert
          variant="light"
          className="text-center w-75 w-sm-50 w-md-50 shadow"
          style={{ backgroundColor: '#ffffffaa', color: '#000' }}
        >
          <Alert.Heading>Your container code:</Alert.Heading>
          <h1>{containerCode}</h1>
        </Alert>
      )}
    </Col>
  );

  const TutorialComponent = () => (
    <Col xs={12} md={5} className="mb-4">
      <div className="tutorial-section p-4 border rounded shadow-sm">
        <h2 className="mb-3">How to Use the Container Code Scanner</h2>
        <ol className="mb-3">
          <li>
            <strong>Upload an Image:</strong> Drag and drop a photo of the container or click to select a file from your device.
          </li>
          <li>
            <strong>Processing:</strong> The model will automatically detect the region containing the serial number and extract the code.
          </li>
          <li>
            <strong>View Results:</strong> Once processed, the detected serial number will be displayed along with any cropped images highlighting the detected regions.
          </li>
          <li>
            <strong>Download or Share:</strong> You can download the results or share them directly from the application.
          </li>
        </ol>
        <div>
          <h5>Tips for Best Results:</h5>
          <ul>
            <li>Ensure the serial number is clearly visible and not obstructed.</li>
            <li>Use high-resolution images for better detection accuracy.</li>
            <li>Avoid blurry or low-light photos to enhance model performance.</li>
          </ul>
        </div>
      </div>
    </Col>
  );

  return (
    <div className="app-container">
      {/* Navbar */}
      <Navbar bg="transparent" variant="light" expand="lg" className="shadow-sm">
        <Container>
          <Navbar.Brand href="#" className="fs-3">
            <i className="bi bi-box-seam me-2"></i>
            Container Code Scanner
          </Navbar.Brand>
        </Container>
      </Navbar>

      {/* Main Content */}
      <Container fluid className="py-5 flex-grow-1">
        <Row className="justify-content-center">
          {/* Prediction Section */}
          <Col xs={12} md={7}>
            <Row className="justify-content-center">
              {/* Test Option */}
              <TestOption />
              {/* Upload Form */}
              <FormComponent imgUrl={imgUrl} handlePicture={handlePicture} />
              {/* Cropped Images */}
              <CroppedImagesComponent
                containerCode={containerCode}
                croppedImages={croppedImages}
              />
              {/* Result */}
              <ResultComponent containerCode={containerCode} searching={searching} />
            </Row>
          </Col>
          {/* Tutorial Section */}
          <TutorialComponent />
        </Row>
      </Container>

      {/* Footer */}
      <footer className="footer bg-transparent text-center py-3 shadow-lg">
        <Container>
          <Row>
            <Col xs={12} md={4} className="mb-3 mb-md-0">
              <p className="mb-1">&copy; 2023 Container Code Scanner. All rights reserved.</p>
            </Col>
            <Col xs={12} md={4} className="mb-3 mb-md-0">
              <p className="mb-1">
                Contact: <a href="mailto:Lukamindjek@gmail.com" className="footer-link">Lukamindjek@gmail.com</a> | +385 095 767 4099
              </p>
            </Col>
            <Col xs={12} md={4}>
              <p className="mb-0">
                <a href="http://www.lukamindek.com" className="footer-link">
                  www.lukamindek.com
                </a>{' '}
                |{' '}
                <a href="http://www.github.com/luksuz" className="footer-link">
                  www.github.com/luksuz
                </a>
              </p>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
}

export default App;
